/**
 * Gorgias live chat (app embed, config/settings_data.json), restyled:
 * - The launcher, to the Figma "Live Chat Button" (node 7782:82833): a 44px
 *   surface-blue square (surface-secondary-blue on hover), 2px radius on its
 *   left corners only, 10px padding around a 24px icon, flush to the
 *   viewport's right edge.
 * - The chat window's background, to a flat surface-blue (the footer's
 *   colour) in place of Gorgias's gradient and noise texture.
 *
 * Gorgias renders both inside same-origin iframes (iframe#chat-button,
 * which holds <button id="gorgias-chat-messenger-button">, and
 * iframe#chat-window), styled by its own Emotion classes. Theme CSS cannot
 * reach into an iframe, so this script injects one <style> into each
 * iframe's document. Gorgias has no setting for launcher shape, icon or a
 * flat window background; only colour and offset are configurable in its
 * dashboard, which is why this lives in the theme.
 *
 * Values are read from the theme's own tokens on <html> at runtime rather
 * than hard-coded, since the iframe document does not inherit them. The
 * icon is the theme's icon-chat.liquid, handed over through the
 * [data-gorgias-launcher-icon] <template> in snippets/gorgias-chat.liquid,
 * painted as a CSS mask so it takes the token colour.
 *
 * Gorgias's own launcher layers (glow, sheen, icon, label, unread badge)
 * are hidden: the design shows the icon only. The launcher keeps its own
 * click behaviour and aria-label untouched.
 *
 * Gorgias normally loads on demand, from the click-to-load launcher in
 * snippets/gorgias-chat-launcher.liquid, which dispatches gorgias-chat:load
 * when it asks for Gorgias; only then does this script start watching, so
 * a visitor who never touches the chat runs no observer here at all. If
 * the app embed is switched back on, its loader tag is on the page and the
 * watch starts at once, as before.
 *
 * Waits for #gorgias-chat-container rather than window.GorgiasChat: the
 * loader arrives some time after this script (on demand, or after
 * content_for_header with the embed on), so neither load order nor
 * DOMContentLoaded says when Gorgias exists. No consent gate applies to
 * Gorgias in this theme. Until the container arrives,
 * a MutationObserver watches <body>'s direct children only (no subtree),
 * which rarely change, and disconnects as soon as it appears. After that,
 * a childList + subtree observer on the container applies the style when
 * Gorgias creates or re-creates either iframe (the window mounts on first
 * open; the launcher is re-created after the chat closes);
 * its callback is a single getElementById. Gorgias's own re-renders happen
 * inside its iframes, which a parent-document observer never sees, so it
 * stays quiet.
 * The iframe's outer position lives in src/css/theme.css.
 */
(function () {
  'use strict';

  var STYLE_ID = 'whittard-chat-style';
  var BUTTON = '#gorgias-chat-messenger-button';
  var tokens = null;

  // Theme tokens, read from <html>: the iframe documents do not inherit
  // them. Only called while buildFrameCss() runs, so every read lands in one
  // synchronous burst (a single style recalculation at most).
  function token(name) {
    if (!tokens) tokens = getComputedStyle(document.documentElement);
    return tokens.getPropertyValue(name).trim();
  }

  // icon-chat.liquid's glyph is not centred in its own 24px viewBox: it is
  // a 17 x 16.333 shape at translate(4, 4), so its centre sits at
  // (12.5, 12.1665). Shifting the viewBox by that offset centres it at the
  // same scale before it becomes the mask. Hard-coded rather than measured
  // with getBBox(), which would force a layout for a value that never
  // changes; the shared icon itself stays untouched (other blocks use it).
  // If icon-chat.liquid's geometry changes, update ICON_VIEWBOX.
  var ICON_VIEWBOX = '0.5 0.1665 24 24';

  function iconUrl() {
    var template = document.querySelector('[data-gorgias-launcher-icon]');
    var source = template && template.content.querySelector('svg');
    if (!source) return 'none';
    var svg = source.cloneNode(true);
    svg.removeAttribute('class');
    svg.removeAttribute('style');
    svg.setAttribute('viewBox', ICON_VIEWBOX);
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    return 'url("data:image/svg+xml,' + encodeURIComponent(svg.outerHTML) + '")';
  }

  // Every declaration has to beat Gorgias's own Emotion rules, so
  // !important is appended here once rather than hand-written on each line.
  function rule(selector, declarations) {
    return selector + '{' + declarations.map(function (d) { return d + ' !important;'; }).join('') + '}';
  }

  function launcherCss() {
    var radius = token('--radius-xs');
    var icon = iconUrl() + ' center / contain no-repeat';

    return [
      // Pinned to the iframe's own box (sized to 44px in src/css/theme.css)
      // rather than left in Gorgias's inline-flex flow, whose wrapper,
      // margin and line box would otherwise push it off-centre.
      rule(BUTTON, [
        'position: fixed', 'inset: 0', 'display: block', 'box-sizing: border-box',
        'width: 100%', 'height: 100%', 'min-width: 0', 'min-height: 0', 'max-width: none',
        'aspect-ratio: auto', 'margin: 0', 'padding: 0', 'border: 0',
        'border-radius: ' + radius + ' 0 0 ' + radius,
        'background: ' + token('--color-surface-blue'),
        'box-shadow: none', 'transform: none', 'overflow: hidden',
        'transition: background-color ' + token('--duration-a3') + ' ' + token('--ease-both')
      ]),
      // Figma State=Hover (7772:169466): surface-secondary-blue, smart-animate
      // 300ms ease-in-out. Hover-capable pointers only, matching Tailwind v4's
      // hover: variant, so a tap on touch screens does not leave it stuck.
      '@media (hover: hover) {' + rule(BUTTON + ':hover', [
        'background: ' + token('--color-surface-secondary-blue')
      ]) + '}',
      // The button fills the iframe edge to edge, so an outer focus ring
      // would be clipped and the theme's global :focus-visible rule cannot
      // reach into the iframe. The ring is drawn inset instead, in the
      // inverted colour so it reads against surface-blue (WCAG 2.4.7).
      rule(BUTTON + ':focus-visible', [
        'outline: ' + token('--spacing-2') + ' solid ' + token('--color-invert'),
        'outline-offset: calc(-2 * ' + token('--spacing-2') + ')'
      ]),
      rule(BUTTON + ' > *', ['display: none']),
      rule(BUTTON + '::after', [
        'content: ""', 'position: absolute', 'inset: 0', 'margin: auto',
        'width: ' + token('--spacing-24'), 'height: ' + token('--spacing-24'),
        'background: ' + token('--color-invert'),
        '-webkit-mask: ' + icon, 'mask: ' + icon
      ]),
      rule('body', ['margin: 0'])
    ].join('\n');
  }

  // Gorgias's window background is a gradient layer, two canvas "glow"
  // layers drawn inside it, and a noise-texture layer. All carry hashed
  // Emotion classes, so they are reached by structure from stable hooks
  // instead. The gradient is the only child of the second wrapper in
  // #self-service-portal-container (the sole match for this selector); the
  // glows are its <div> children that hold a <canvas>; the noise is the
  // first <div> of the window root, which carries Gorgias's stable widget-*
  // size class.
  var WINDOW_BACKGROUND = '#self-service-portal-container > * > :only-child';

  function windowCss() {
    return [
      rule(WINDOW_BACKGROUND, [
        'background: ' + token('--color-surface-blue')
      ]),
      rule(WINDOW_BACKGROUND + ' > div:has(> canvas)', ['display: none']),
      rule('.frame-content > * > [class*="widget-"] > div:first-of-type', [
        'background-image: none'
      ])
    ].join('\n');
  }

  var FRAMES = { 'chat-button': launcherCss, 'chat-window': windowCss };
  var frameCss = null;

  // Builds every frame's CSS together on first use (the launcher mount), then
  // keeps only plain strings. Building the window's CSS later, on first open,
  // made its token reads force a fresh style recalculation on that click
  // (about 17 ms on desktop in the performance check).
  function buildFrameCss() {
    frameCss = {};
    for (var id in FRAMES) frameCss[id] = FRAMES[id]();
    tokens = null;
  }

  function styleFrame(iframe) {
    var doc = iframe.contentDocument;
    if (!doc || !doc.head || doc.getElementById(STYLE_ID)) return;
    if (!frameCss) buildFrameCss();
    var style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = frameCss[iframe.id];
    doc.head.appendChild(style);
    // The launcher stand-in hands over once this frame has its content.
    document.dispatchEvent(new CustomEvent('gorgias-chat:frame-styled', { detail: { iframe: iframe } }));
  }

  function check() {
    for (var id in FRAMES) {
      var iframe = document.getElementById(id);
      if (!iframe) continue;
      styleFrame(iframe);
      if (iframe.__whittardBound) continue;
      iframe.__whittardBound = true;
      // Gorgias writes the iframe document after mount; catch that write too.
      iframe.addEventListener('load', function (event) { styleFrame(event.currentTarget); });
    }
  }

  // Lifts the launcher above the PDP's mobile / tablet sticky Add-to-Bag bar
  // while that bar is showing. Both bar controllers
  // (assets/product-sticky-atb.js and the custom-bundle one) already flag
  // the bar with data-visible, so this observes that attribute instead of
  // adding code to either.
  //
  // The controllers re-write data-visible on every scroll frame, even with
  // the same value, and a MutationObserver fires for those too. So the
  // attribute callback only compares strings and never reads layout; bar
  // heights come solely from the ResizeObserver, which reports them after
  // layout. The offset is written to #gorgias-chat-container and the
  // launcher stand-in (the only consumers) rather than <html>, so a change
  // restyles just those, and only when the value changes.
  var stickyOffset = 0;

  function applyStickyOffset() {
    var targets = [
      document.getElementById('gorgias-chat-container'),
      document.querySelector('[data-chat-launcher]')
    ];
    for (var i = 0; i < targets.length; i++) {
      if (!targets[i]) continue;
      if (stickyOffset) {
        targets[i].style.setProperty('--sticky-atb-mobile-offset', stickyOffset + 'px');
      } else {
        targets[i].style.removeProperty('--sticky-atb-mobile-offset');
      }
    }
  }

  function trackStickyBars() {
    var bars = document.querySelectorAll('[data-sticky-atb-mobile]');
    if (!bars.length || !window.ResizeObserver) return;
    var heights = [];
    var visibleKey = '';

    function sync() {
      var offset = 0;
      for (var i = 0; i < bars.length; i++) {
        if (bars[i].getAttribute('data-visible') === 'true') {
          offset = Math.max(offset, heights[i] || 0);
        }
      }
      if (offset === stickyOffset) return;
      stickyOffset = offset;
      applyStickyOffset();
    }

    // Same-value writes land here on every scroll frame: string compares
    // only, and nothing further unless a bar actually showed or hid.
    var attributeObserver = new MutationObserver(function () {
      var key = '';
      for (var i = 0; i < bars.length; i++) key += bars[i].getAttribute('data-visible');
      if (key === visibleKey) return;
      visibleKey = key;
      sync();
    });

    var sizeObserver = new ResizeObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var index = Array.prototype.indexOf.call(bars, entries[i].target);
        var box = entries[i].borderBoxSize && entries[i].borderBoxSize[0];
        heights[index] = box ? box.blockSize : entries[i].target.offsetHeight;
      }
      sync();
    });

    for (var i = 0; i < bars.length; i++) {
      attributeObserver.observe(bars[i], { attributes: true, attributeFilter: ['data-visible'] });
      sizeObserver.observe(bars[i]);
    }
  }

  function watch(container) {
    // A bar can already be showing before Gorgias mounts.
    applyStickyOffset();
    check();
    new MutationObserver(check).observe(container, { childList: true, subtree: true });
  }

  var waiting = false;

  function waitForContainer() {
    if (waiting) return;
    waiting = true;
    var container = document.getElementById('gorgias-chat-container');
    if (container) {
      watch(container);
      return;
    }
    var bodyObserver = new MutationObserver(function () {
      var found = document.getElementById('gorgias-chat-container');
      if (!found) return;
      bodyObserver.disconnect();
      watch(found);
    });
    bodyObserver.observe(document.body, { childList: true });
  }

  function start() {
    trackStickyBars();
    if (document.querySelector('script[data-gorgias-loader-chat]')) waitForContainer();
  }

  document.addEventListener('gorgias-chat:load', waitForContainer);

  // The theme editor re-renders a section's bars on reload.
  document.addEventListener('shopify:section:load', trackStickyBars);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
